import { api } from '../lib/axios';

export type IssueReportCategory = 'GENERAL_ISSUE' | 'BUG' | 'IMPROVEMENT' | 'FEATURE_REQUEST';
export type IssueReportStatus = 'SUBMITTED' | 'UNDER_REVIEW' | 'PLANNED' | 'IN_PROGRESS' | 'IMPLEMENTED' | 'CLOSED';
export type IssueReportPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type IssueReportReporterType = 'MANUFACTURER' | 'OPERATIONS_MANAGER' | 'ADMIN';

export interface IssueReport {
  id: string;
  category: IssueReportCategory;
  title: string;
  description: string;
  affectedArea: string | null;
  stepsToReproduce: string | null;
  expectedOutcome: string | null;
  status: IssueReportStatus;
  adminPriority: IssueReportPriority;
  adminNotes: string | null;
  resolutionSummary: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  manufacturer: {
    id: string;
    name: string;
  };
  reporter: {
    id: string;
    fullName: string;
    email: string;
    reporterType: IssueReportReporterType;
  };
  reviewer: {
    id: string;
    fullName: string;
    email: string;
  } | null;
}

export interface IssueReportSummary {
  total: number;
  byStatus: Record<IssueReportStatus, number>;
  byPriority: Record<IssueReportPriority, number>;
}

export interface CreateIssueReportPayload {
  category: IssueReportCategory;
  title: string;
  description: string;
  affectedArea?: string;
  stepsToReproduce?: string;
  expectedOutcome?: string;
}

export interface UpdateIssueReportPayload {
  status?: IssueReportStatus;
  adminPriority?: IssueReportPriority;
  adminNotes?: string;
  resolutionSummary?: string;
}

export interface IssueReportFilters {
  status?: IssueReportStatus;
  category?: IssueReportCategory;
  priority?: IssueReportPriority;
  search?: string;
}

const buildParams = (filters: IssueReportFilters) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => typeof value === 'string' && value.trim().length > 0),
  );

class IssueReportService {
  async submitIssueReport(payload: CreateIssueReportPayload): Promise<IssueReport> {
    const response = await api.post('/issue-reports', payload);
    return response.data.data;
  }

  async getManufacturerIssueReports(filters: IssueReportFilters = {}): Promise<IssueReport[]> {
    const response = await api.get('/issue-reports', { params: buildParams(filters) });
    return response.data.data;
  }

  async getAdminIssueReports(filters: IssueReportFilters = {}): Promise<IssueReport[]> {
    const response = await api.get('/admin/issue-reports', { params: buildParams(filters) });
    return response.data.data;
  }

  async getIssueReportSummary(): Promise<IssueReportSummary> {
    const response = await api.get('/admin/issue-reports/summary');
    return response.data.data;
  }

  async updateIssueReport(id: string, payload: UpdateIssueReportPayload): Promise<IssueReport> {
    const response = await api.patch(`/admin/issue-reports/${id}`, payload);
    return response.data.data;
  }
}

export const issueReportService = new IssueReportService();
